import { Check, Sparkles, Star, Zap, Clock, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    // Fixed end date: January 31, 2026
    const endDate = new Date('2026-01-31T23:59:59');

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = endDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="grid grid-cols-4 gap-2 text-center">
      {[
        { value: timeLeft.days, label: 'Days' },
        { value: timeLeft.hours, label: 'Hrs' },
        { value: timeLeft.minutes, label: 'Min' },
        { value: timeLeft.seconds, label: 'Sec' },
      ].map((item) => (
        <div key={item.label} className="bg-background/50 rounded-md p-2">
          <div className="text-xl font-bold text-primary">{String(item.value).padStart(2, '0')}</div>
          <div className="text-[10px] text-muted-foreground uppercase">{item.label}</div>
        </div>
      ))}
    </div>
  );
};

const Pricing = () => {
  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Perfect for personal use",
      features: [
        "All phonetic alphabets (IPA, NATO, regional)",
        "Real-time text conversion",
        "Copy to clipboard",
        "Dark & light themes",
        "100% local processing",
        "Basic support",
      ],
      cta: "Install Free",
      href: "https://chrome.google.com/webstore",
      popular: false,
      isLimited: false,
    },
    {
      name: "Pro",
      price: "$3.99",
      period: "month",
      description: "For professionals & educators",
      features: [
        "Everything in Free",
        "Batch text conversion",
        "Export to PDF/Word",
        "Custom pronunciation guides",
        "Multilingual support",
        "Priority support",
        "Early access to features",
      ],
      cta: "Get Pro",
      href: "https://gumroad.com",
      popular: true,
      isLimited: false,
    },
    {
      name: "Lifetime Deal",
      price: "$14.99",
      originalPrice: "$49.99",
      period: "one-time",
      description: "Limited time offer — 70% off",
      features: [
        "Everything in Pro",
        "Lifetime updates included",
        "All future features",
        "Priority feature requests",
        "Founding member badge",
        "VIP Discord access",
        "No recurring fees ever",
      ],
      cta: "Grab LTD Now",
      href: "https://gumroad.com",
      popular: false,
      isLimited: true,
      spotsLeft: 31,
    }
  ];

  return (
    <section id="pricing" className="py-24 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm text-muted-foreground mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Simple pricing
          </div>
          <h2 className="text-3xl font-semibold mb-3">
            Choose Your Plan
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Start free, upgrade when you need more.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              className={`relative p-6 rounded-lg border transition-all duration-300 animate-fade-in hover:-translate-y-1 hover:shadow-lg ${
                plan.popular 
                  ? 'bg-card border-foreground/20 shadow-lg' 
                  : plan.isLimited
                  ? 'bg-gradient-to-b from-card to-primary/5 border-primary/30 hover:border-primary/50'
                  : 'bg-card border-border hover:border-foreground/20'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-foreground text-background text-xs font-medium">
                    <Star className="w-3 h-3" />
                    Most Popular
                  </div>
                </div>
              )}

              {plan.isLimited && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium animate-pulse">
                    <Flame className="w-3 h-3" />
                    Limited Time
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-lg font-medium mb-1">{plan.name}</h3>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
              </div>

              <div className="mb-6">
                {plan.originalPrice && (
                  <span className="text-lg text-muted-foreground line-through mr-2">{plan.originalPrice}</span>
                )}
                <span className="text-4xl font-semibold">{plan.price}</span>
                <span className="text-muted-foreground ml-2">/{plan.period}</span>
              </div>

              {plan.isLimited && (
                <div className="mb-4 space-y-3">
                  <div className="p-2 rounded-md bg-primary/10 border border-primary/20">
                    <div className="flex items-center gap-2 text-sm mb-2">
                      <Clock className="w-4 h-4 text-primary" />
                      <span className="text-primary font-medium">
                        Only {plan.spotsLeft} spots left!
                      </span>
                    </div>
                    <CountdownTimer />
                  </div>
                </div>
              )}

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 mt-0.5 text-foreground" />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className={`w-full ${plan.isLimited ? 'bg-primary hover:bg-primary/90' : ''}`}
                variant={plan.popular ? "default" : plan.isLimited ? "default" : "outline"}
                asChild
              >
                <a href={plan.href} target="_blank" rel="noopener noreferrer">
                  {plan.cta}
                  {plan.popular && <Zap className="w-4 h-4 ml-2" />}
                  {plan.isLimited && <Flame className="w-4 h-4 ml-2" />}
                </a>
              </Button>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          Cancel Pro anytime. LTD includes lifetime updates with no recurring fees.
        </p>
      </div>
    </section>
  );
};

export default Pricing;